/**
 * Vuex store for the upstream refining contract catalog (UCG-2210
 * contract-management slice).
 *
 * Source-of-truth for the contracts + partners the master lot builder
 * demo reads from. Persists across page reloads via `vuex-persist`
 * as the `upstreamContracts` module (registered in `store/index.js`
 * modules list).
 *
 * Architecture: this module owns the persisted contract+partner catalog.
 * On first load it bootstraps from the in-bundle static fixtures via the
 * mock-api seam (`fetchUpstreamRefiningContracts` / `fetchRefiningPartners`).
 * Admin CRUD on contracts thereafter mutates the persisted state through
 * the matching mock-api methods (`createContract`,
 * `updateContract`, `setContractActive`).
 *
 * Partners are intentionally read-only in the demo — they're rarely-changing
 * master data; CRUD on them was deliberately deferred (see the wiki plan).
 *
 * When the v1 backend lands, each mock-api call becomes a real `request.*`
 * call against the matching endpoint, this module comes off the
 * `vuex-persist` modules list, and action signatures stay identical.
 */

import {
  isContractEffective,
  isContractSelectableForSwitch,
} from '@/util/refinery/upstream/upstreamContract'
import {
  fetchUpstreamRefiningContracts as apiFetchContracts,
  fetchRefiningPartners as apiFetchPartners,
  createContract as apiCreateContract,
  updateContract as apiUpdateContract,
  setContractActive as apiSetContractActive,
} from '@/api/refinery/upstreamMasterLotMockApi'

const getters = {
  getContracts: (state) =>
    Object.values(state.contracts).sort((a, b) => {
      const at = (a?.title || '').toLowerCase()
      const bt = (b?.title || '').toLowerCase()
      return at.localeCompare(bt)
    }),
  getActiveContracts: (_state, accessors) =>
    accessors.getContracts.filter((contract) => isContractEffective(contract)),
  getSelectableContractsForSwitch: (_state, accessors) =>
    accessors.getContracts.filter((contract) => isContractSelectableForSwitch(contract)),
  getContractById: (state) => (id) => state.contracts[id] || null,
  getRefiningPartners: (state) => state.partners,
  isCatalogSeeded: (state) => state.seeded,
}

const actions = {
  async bootstrapIfEmpty({commit, state}) {
    if (state.seeded) return Object.values(state.contracts)
    if (Object.keys(state.contracts).length > 0) {
      commit('SET_SEEDED', true)
      return Object.values(state.contracts)
    }
    const [contracts, partners] = await Promise.all([
      apiFetchContracts(),
      apiFetchPartners(),
    ])
    commit('SET_CONTRACTS', contracts)
    commit('SET_REFINING_PARTNERS', partners)
    commit('SET_SEEDED', true)
    return contracts
  },

  async fetchContracts({state, dispatch}) {
    if (!state.seeded || Object.keys(state.contracts).length === 0) {
      await dispatch('bootstrapIfEmpty')
    }
    return Object.values(state.contracts)
  },

  async fetchRefiningPartners({state, dispatch}) {
    if (!state.seeded || state.partners.length === 0) {
      await dispatch('bootstrapIfEmpty')
    }
    return state.partners
  },

  // Draft validation lives at the api seam (`upstreamMasterLotMockApi`,
  // mirroring the v1 backend). The store stays thin: dispatch → api → upsert.
  async createContract({commit}, draft) {
    const contract = await apiCreateContract(draft)
    commit('UPSERT_CONTRACT', contract)
    return contract
  },

  async updateContract({commit, state}, {id, draft}) {
    const existing = state.contracts[id]
    if (!existing) throw new Error(`upstream refining contract not found: ${id}`)
    const contract = await apiUpdateContract(existing, draft)
    commit('UPSERT_CONTRACT', contract)
    return contract
  },

  async setContractActive({commit, state}, {id, active}) {
    const existing = state.contracts[id]
    if (!existing) throw new Error(`upstream refining contract not found: ${id}`)
    const contract = await apiSetContractActive(existing, active)
    commit('UPSERT_CONTRACT', contract)
    return contract
  },

  async resetCatalog({commit}) {
    commit('RESET_STATE')
    const [contracts, partners] = await Promise.all([
      apiFetchContracts(),
      apiFetchPartners(),
    ])
    commit('SET_CONTRACTS', contracts)
    commit('SET_REFINING_PARTNERS', partners)
    commit('SET_SEEDED', true)
    return contracts
  },
}

const mutations = {
  SET_CONTRACTS(state, contracts) {
    const next = {}
    for (const contract of contracts || []) {
      if (contract && contract.id) next[contract.id] = contract
    }
    state.contracts = next
  },
  UPSERT_CONTRACT(state, contract) {
    if (!contract || !contract.id) return
    state.contracts = {...state.contracts, [contract.id]: contract}
  },
  SET_REFINING_PARTNERS(state, partners) {
    state.partners = Array.isArray(partners) ? partners : []
  },
  SET_SEEDED(state, value) {
    state.seeded = Boolean(value)
  },
  RESET_STATE(state) {
    Object.assign(state, getDefaultState())
  },
}

export default {
  namespaced: true,
  state: getDefaultState,
  getters,
  actions,
  mutations,
}

function getDefaultState() {
  return {
    contracts: {},
    partners: [],
    seeded: false,
  }
}

export const __testing__ = {
  getDefaultState,
}
